import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react-router';
import { DialogFooter, DialogHeader, DialogTitle } from '../../../../common/components/ui/dialog';
import { ScrollArea } from '../../../../common/components/ui/scroll-area.tsx';
import { captureError } from '../../../../tracking/logger.ts';
import { LoadingButton } from '../../../../common/components/LoadingButton.tsx';
import { Button } from '../../../../common/components/ui/button';
import { CartDialogConfirmCancel } from '../shared/CartDialogConfirmCancel';
import { useCartErrorNotification } from '../../state-hooks/useCartErrorNotification';
import { toKeyedItems } from '../../../../common/utils/keyedItems';

export function CartDialogError() {
  const { t } = useTranslation();
  const { messages, traceId } = useCartErrorNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  function handleShoppingCartReload() {
    setIsLoading(true);
    window.location.reload();
  }

  useEffect(() => {
    captureError('Cart Dialog Error: The dialog `Fehler im Warenkorb` is shown to the user!', messages);
    Sentry.logger.error(
      `Cart Dialog Error: The dialog "Fehler im Warenkorb" is shown to the user! ${messages.join(' - ')}`
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (showConfirmation) {
    return <CartDialogConfirmCancel onBack={() => setShowConfirmation(false)} />;
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle className={'flex items-center mb-0'}>{t('Fehler im Warenkorb')}</DialogTitle>
        <div className={'mt-2 text-xs text-muted-foreground'}>
          {new Date().toLocaleString()}
          {traceId && (
            <>
              {' | ID: '}
              <code>{traceId}</code>
            </>
          )}
        </div>
      </DialogHeader>
      <ScrollArea className="overflow-y-auto">
        <div>
          {messages && messages.length > 0 && messages[0].length > 0 ? (
            <ul className="list-none space-y-2">
              {toKeyedItems(messages).map(({ key, item: message }) => (
                <li key={key}>{message}</li>
              ))}
            </ul>
          ) : (
            <span>{t('Beim Verarbeiten des Warenkorbs ist ein unerwarteter Fehler aufgetreten')}!</span>
          )}
        </div>
      </ScrollArea>
      <DialogFooter className={'mt-4 flex-col desktop:flex-row gap-4 desktop:gap-1'}>
        <Button variant={'primaryOutline'} onClick={() => setShowConfirmation(true)} data-testid={'cart-cancel-button'}>
          {t('Warenkorb verwerfen')}!
        </Button>
        <LoadingButton loading={isLoading} onClick={handleShoppingCartReload} data-testid={'cart-reload-button'}>
          {t('Warenkorb neu laden')}!
        </LoadingButton>
      </DialogFooter>
    </>
  );
}
